package fr.frux.feature.site.detail

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.KeyboardArrowRight
import androidx.compose.material.icons.outlined.Directions
import androidx.compose.material.icons.outlined.Place
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import androidx.lifecycle.Lifecycle
import androidx.lifecycle.compose.LifecycleEventEffect
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.frux.core.common.error.AppError
import fr.frux.core.common.format.formatCoordinates
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxFavoriteButton
import fr.frux.core.designsystem.components.FruxMinTouchTarget
import fr.frux.core.designsystem.components.FruxSnackbar
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.label
import fr.frux.feature.site.labelRes
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_access_title
import fr.frux.feature.site.resources.site_added_by
import fr.frux.feature.site.resources.site_added_by_just_now
import fr.frux.feature.site.resources.site_detail_kind
import fr.frux.feature.site.resources.site_detail_title
import fr.frux.feature.site.resources.site_directions
import fr.frux.feature.site.resources.site_favorite_add
import fr.frux.feature.site.resources.site_favorite_remove
import fr.frux.feature.site.resources.site_not_found_back
import fr.frux.feature.site.resources.site_not_found_message
import fr.frux.feature.site.resources.site_not_found_title
import fr.frux.feature.site.resources.site_planting_add
import fr.frux.feature.site.resources.site_plantings_hint
import fr.frux.feature.site.resources.site_plantings_title
import fr.frux.feature.site.resources.site_rule_notice_ask
import fr.frux.feature.site.resources.site_rule_notice_ask_manager
import fr.frux.feature.site.resources.site_rule_notice_open
import fr.frux.feature.site.resources.site_rule_notice_private
import fr.frux.feature.site.resources.site_steward_manager
import fr.frux.feature.site.resources.site_steward_none
import fr.frux.feature.site.resources.site_steward_owner
import fr.frux.feature.site.resources.site_trees_add_hint
import fr.frux.feature.site.resources.site_trees_count
import fr.frux.feature.site.resources.site_trees_none
import fr.frux.feature.site.resources.site_trees_title
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.SiteAccessRule
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.allowsContribution
import fr.frux.shared.domain.model.badge
import fr.frux.shared.domain.model.isLocked
import fr.frux.shared.domain.model.orderedPlantings
import fr.frux.shared.presentation.relativeAgeLabel
import fr.frux.shared.presentation.toFruxStatus
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

/**
 * Fiche d'un lieu de cueillette (ADR 0035, maquette FicheLieu), ouverte à l'anonyme.
 */
@Composable
fun SiteDetailScreen(
    siteId: Long,
    onBack: () -> Unit,
    onTreeClick: (Long) -> Unit,
    onLogin: () -> Unit,
    onDirections: (Double, Double, String) -> Unit,
    onDeleted: () -> Unit,
    onStartHarvest: (Long) -> Unit,
    onHarvestClick: (Long) -> Unit,
    viewModel: SiteDetailViewModel = rememberSiteDetailViewModel(siteId),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val harvests by viewModel.harvests.state.collectAsStateWithLifecycle()
    val ownership by viewModel.ownership.state.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    ResumeRefreshEffect { viewModel.harvests.refresh(siteId) }
    SiteDetailEventsEffect(viewModel, onLogin = onLogin, onDeleted = onDeleted)
    SiteDetailMessageEffect(state.message, snackbarHostState, onShown = viewModel::consumeMessage)
    SiteDetailOverlays(state, viewModel)

    Scaffold(
        topBar = {
            FruxTopBar(
                title = stringResource(Res.string.site_detail_title),
                onBack = onBack,
                actions = {
                    state.site?.let { site ->
                        FruxFavoriteButton(
                            isFavorite = site.isFavorite,
                            onClick = viewModel::toggleFavorite,
                            addLabel = stringResource(Res.string.site_favorite_add),
                            removeLabel = stringResource(Res.string.site_favorite_remove),
                        )
                    }
                },
            )
        },
        snackbarHost = { SnackbarHost(snackbarHostState) { FruxSnackbar(it) } },
        containerColor = MaterialTheme.colorScheme.background,
    ) { padding ->
        val site = state.site
        when {
            site != null -> {
                SiteDetailContent(
                    site = site,
                    state = state,
                    harvests = harvests,
                    ownership = ownership,
                    onClaim = { viewModel.ownership.claim(siteId) },
                    onRuleSelected = { viewModel.ownership.setAccessRule(siteId, it) },
                    onUnclaim = viewModel.ownership::askUnclaim,
                    onTreeClick = onTreeClick,
                    onHarvestClick = onHarvestClick,
                    onFavorite = viewModel::toggleFavorite,
                    onStartHarvest = {
                        when (viewModel.harvests.requestStart(site)) {
                            StartOutcome.Open -> onStartHarvest(siteId)
                            StartOutcome.LoginRequired -> onLogin()
                            StartOutcome.Locked -> Unit
                        }
                    },
                    onDirections = onDirections,
                    onAddPlanting = viewModel::openAddPlanting,
                    onReport = viewModel::openReport,
                    onDelete = viewModel::requestDelete,
                    modifier = Modifier.padding(padding),
                )
            }

            state.isLoading -> {
                LoadingState(Modifier.padding(padding))
            }

            state.error is AppError.NotFound -> {
                EmptyState(
                    icon = Icons.Outlined.Place,
                    title = stringResource(Res.string.site_not_found_title),
                    message = stringResource(Res.string.site_not_found_message),
                    ctaLabel = stringResource(Res.string.site_not_found_back),
                    onCta = onBack,
                    modifier = Modifier.padding(padding),
                )
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::retry,
                    modifier = Modifier.padding(padding),
                )
            }
        }
    }
}

/**
 * Rejoue [action] à chaque reprise. L'entrée est recréée au retour d'un écran fils alors que le
 * ViewModel survit : aucun saut du premier resume, c'est le contrôleur qui ignore l'appel sans lieu.
 */
@Composable
private fun ResumeRefreshEffect(action: () -> Unit) {
    val current by rememberUpdatedState(action)
    LifecycleEventEffect(Lifecycle.Event.ON_RESUME) { current() }
}

@Composable
private fun SiteDetailEventsEffect(
    viewModel: SiteDetailViewModel,
    onLogin: () -> Unit,
    onDeleted: () -> Unit,
) {
    val currentOnLogin by rememberUpdatedState(onLogin)
    val currentOnDeleted by rememberUpdatedState(onDeleted)
    LaunchedEffect(viewModel) {
        viewModel.events.collect { event ->
            when (event) {
                SiteDetailEvent.LoginRequired -> currentOnLogin()
                SiteDetailEvent.Deleted -> currentOnDeleted()
            }
        }
    }
}

@Composable
private fun SiteDetailContent(
    site: Site,
    state: SiteDetailState,
    harvests: SiteHarvestsUi,
    ownership: SiteOwnershipUi,
    onClaim: () -> Unit,
    onRuleSelected: (SiteAccessRule) -> Unit,
    onUnclaim: () -> Unit,
    onTreeClick: (Long) -> Unit,
    onHarvestClick: (Long) -> Unit,
    onFavorite: () -> Unit,
    onStartHarvest: () -> Unit,
    onDirections: (Double, Double, String) -> Unit,
    onAddPlanting: () -> Unit,
    onReport: () -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.md),
    ) {
        SiteHeader(site)
        val now = Clock.System.now().toEpochMilliseconds()
        val locked = site.isLocked(now)
        // Le verrou parle aux visiteurs : le propriétaire garde la règle et lance sa récolte.
        if (locked && !site.isOwner) {
            SitePrivateNotice(isFavorite = site.isFavorite, onFavorite = onFavorite)
        } else {
            RuleNotice(site)
        }
        // Verrouillé et pas à moi : rien à lister ni à lancer, le verrou dit tout.
        if (!locked || site.allowsContribution()) {
            SiteHarvestsSection(
                upcoming = harvests.upcoming,
                onHarvestClick = onHarvestClick,
                onStartHarvest = onStartHarvest.takeIf { site.allowsContribution() },
                highlightFirst = site.accessRule == SiteAccessRule.PRIVATE,
                isLoading = harvests.isLoading,
            )
        }
        PlantingsSection(
            site = site,
            lookalikes = state.lookalikes,
            onAddPlanting = onAddPlanting.takeIf { state.isSignedIn && site.allowsContribution() },
        )
        HorizontalDivider()
        TreesSection(site, onTreeClick)
        HorizontalDivider()
        AccessSection(site, onDirections)
        ImportedSiteSource.fromUsername(site.createdByUsername)?.let {
            HorizontalDivider()
            SiteImportSourceSection(it)
        }
        SiteOwnerSection(
            site = site,
            ui = ownership,
            onClaim = onClaim,
            onRuleSelected = onRuleSelected,
            onUnclaim = onUnclaim,
        )
        SiteModerationActions(site = site, isMine = state.isMine, onReport = onReport, onDelete = onDelete)
    }
}

@Composable
private fun SiteHeader(site: Site) {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        Row(horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
            Pill(stringResource(site.accessRule.labelRes()))
            Pill(stringResource(Res.string.site_detail_kind))
        }
        Text(
            text = site.name,
            style = MaterialTheme.typography.headlineSmall,
            modifier = Modifier.semantics { heading() },
        )
        // Un lieu importé n'a pas d'auteur : le compte d'import n'est personne, la source se nomme.
        val importSource = ImportedSiteSource.fromUsername(site.createdByUsername)
        Text(
            text = if (importSource != null) stringResource(importSource.labelRes()) else addedByLine(site),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun Pill(text: String) {
    Surface(shape = RoundedCornerShape(50), color = MaterialTheme.colorScheme.secondaryContainer) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
            modifier = Modifier.padding(horizontal = FruxSpacing.sm, vertical = FruxSpacing.xs),
        )
    }
}

private const val AGO_PREFIX = "il y a "

/** « Ajouté par Théo il y a 12 j · Personne ne s'en occupe », réduit à la tutelle sans auteur ni date. */
@OptIn(ExperimentalTime::class)
@Composable
private fun addedByLine(site: Site): String {
    val manager = site.managerName
    val steward =
        when {
            manager != null -> stringResource(Res.string.site_steward_manager, manager)
            site.hasOwner -> stringResource(Res.string.site_steward_owner)
            else -> stringResource(Res.string.site_steward_none)
        }
    val author = site.createdByUsername
    val createdAt = site.createdAtMillis
    if (author == null || createdAt == null) return steward
    // Seul formateur de fraîcheur de l'app (#420) : on en garde la durée, la phrase porte « il y a ».
    val age = relativeAgeLabel(createdAt, Clock.System.now().toEpochMilliseconds())
    return when {
        age.startsWith(
            AGO_PREFIX,
        ) -> stringResource(Res.string.site_added_by, author, age.removePrefix(AGO_PREFIX), steward)

        else -> stringResource(Res.string.site_added_by_just_now, author, steward)
    }
}

@Composable
private fun RuleNotice(site: Site) {
    val text =
        when (site.accessRule) {
            SiteAccessRule.OPEN -> {
                stringResource(Res.string.site_rule_notice_open)
            }

            SiteAccessRule.ASK -> {
                site.managerName?.let { stringResource(Res.string.site_rule_notice_ask_manager, it) }
                    ?: stringResource(Res.string.site_rule_notice_ask)
            }

            SiteAccessRule.PRIVATE -> {
                stringResource(Res.string.site_rule_notice_private)
            }
        }
    Surface(
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.surfaceVariant,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.bodyMedium,
            modifier = Modifier.padding(FruxSpacing.md),
        )
    }
}

@Composable
private fun SectionTitle(
    title: String,
    subtitle: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.semantics { heading() },
        )
        Text(
            text = subtitle,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun PlantingsSection(
    site: Site,
    lookalikes: Map<Long, List<SpeciesLookalike>>,
    onAddPlanting: (() -> Unit)?,
) {
    Column {
        SectionTitle(stringResource(Res.string.site_plantings_title), stringResource(Res.string.site_plantings_hint))
        site.orderedPlantings().forEach { planting ->
            PlantingRow(planting = planting, lookalikes = lookalikes[planting.species.id].orEmpty())
        }
        onAddPlanting?.let {
            FruxButton(
                text = stringResource(Res.string.site_planting_add),
                onClick = it,
                style = FruxButtonStyle.Text,
                icon = FruxIcons.Add,
            )
        }
    }
}

@Composable
private fun TreesSection(
    site: Site,
    onTreeClick: (Long) -> Unit,
) {
    val subtitle =
        site.badge()?.takeIf { site.treesCount > 0 }?.let { badge ->
            val estimate = if (badge.atLeast) "${badge.count}+" else badge.count.toString()
            stringResource(Res.string.site_trees_count, site.treesCount, estimate)
        } ?: stringResource(Res.string.site_trees_none)
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
        SectionTitle(stringResource(Res.string.site_trees_title), subtitle)
        site.trees.forEach { tree -> TreeRow(tree, onClick = { onTreeClick(tree.id) }) }
        Text(
            text = stringResource(Res.string.site_trees_add_hint),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun TreeRow(
    tree: Tree,
    onClick: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = FruxMinTouchTarget)
                .clickable(onClick = onClick)
                .padding(vertical = FruxSpacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(text = tree.species.commonNameFr, style = MaterialTheme.typography.bodyLarge)
            Text(
                text = tree.status.toFruxStatus().label,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Icon(
            imageVector = Icons.AutoMirrored.Outlined.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun AccessSection(
    site: Site,
    onDirections: (Double, Double, String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        Text(
            text = stringResource(Res.string.site_access_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.semantics { heading() },
        )
        site.description?.takeIf { it.isNotBlank() }?.let { description ->
            Text(text = description, style = MaterialTheme.typography.bodyMedium)
        }
        Text(
            text = formatCoordinates(site.latitude, site.longitude),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        FruxButton(
            text = stringResource(Res.string.site_directions),
            onClick = { onDirections(site.latitude, site.longitude, site.name) },
            style = FruxButtonStyle.Tonal,
            icon = Icons.Outlined.Directions,
        )
    }
}
